/**
 * Pure helpers for the Student/Family invite flow (ENG-9203).
 *
 * Centralizes (and makes unit-testable) the logic that turns a chosen invite
 * SCOPE (all students, a grade, or a roster) + the GET /portal student list into
 * the concrete `student_ids` passed to POST /spa-users/invites, plus the
 * "most recent invitation date" used for the within-2-days re-invite confirm.
 */

import type { PortalStudent } from '@liveschool/types';

/** Invite audience — mirrors the backend `user_type`. */
export type InviteAudience = 'student' | 'family';

/**
 * The chosen invite scope. `all` = every portal student (exclusive — can't be
 * combined). `rosters` = one or more grades/homerooms; the student ids are the
 * UNION of those rosters' members, resolved from the coreStore rosters.
 */
export type InviteScope =
  | { kind: 'all' }
  | { kind: 'rosters'; rosterIds: string[] };

/** Window (ms) within which a prior invite triggers the re-invite confirm dialog. */
export const REINVITE_CONFIRM_WINDOW_MS = 2 * 24 * 60 * 60 * 1000;

/**
 * Normalize a coreStore roster's membership to a set of string student ids.
 *
 * Roster membership comes in two shapes across the codebase: an array of
 * primitive ids (number|string) or an array of `{ id?, student_id? }` objects
 * (mirrors the normalizer in StudentsPage). Ids are coerced with String() so
 * they match `PortalStudent.student_id` (always a string).
 */
export function resolveRosterStudentIds(roster: unknown): Set<string> {
  const ids = new Set<string>();
  const members = (roster as { students?: unknown; student_ids?: unknown } | null)?.students
    ?? (roster as { student_ids?: unknown } | null)?.student_ids;
  if (!Array.isArray(members)) return ids;
  for (const member of members) {
    if (member == null) continue;
    if (typeof member === 'number' || typeof member === 'string') {
      ids.add(String(member));
    } else if (typeof member === 'object') {
      const obj = member as { id?: number | string; student_id?: number | string };
      const raw = obj.student_id ?? obj.id;
      if (raw != null) ids.add(String(raw));
    }
  }
  return ids;
}

/** A coreStore roster, matched by either `id` or `roster_id`. */
interface RosterLike {
  id?: number | string;
  roster_id?: number | string;
}

/**
 * Resolve a (possibly multi-roster) {@link InviteScope} to the set of student
 * ids it targets, or null for the whole school ('all'). Multiple rosters are
 * unioned. Unknown roster ids are skipped.
 */
export function resolveScopeStudentIds(
  scope: InviteScope,
  rosters: RosterLike[]
): Set<string> | null {
  if (scope.kind === 'all') return null;
  const ids = new Set<string>();
  for (const rosterId of scope.rosterIds) {
    const roster = rosters.find(
      (r) => String(r.id) === rosterId || String(r.roster_id) === rosterId
    );
    if (!roster) continue;
    for (const id of resolveRosterStudentIds(roster)) ids.add(id);
  }
  return ids;
}

/** Whether a portal student falls within the given scope's id set (null = all). */
function inScope(student: PortalStudent, scopeIds: Set<string> | null): boolean {
  return scopeIds == null || scopeIds.has(student.student_id);
}

/**
 * Student ids for a bulk STUDENT invite (the "Invite N" action).
 *
 * Eligibility is decided by AAS and delivered as `student_user.is_inviteable`
 * (ENG-9657): the account exists but has never had a password set on the new
 * SPA platform — migrated, or created by a CSV / Clever / ClassLink import —
 * and has a deliverable email.
 *
 * This deliberately does NOT re-derive the rule from `last_login` / `email`.
 * It used to, and the copy drifted from the send path: migrated accounts carry
 * a pre-migration `last_login`, so the client filtered out people AAS would
 * have happily emailed, and the control vanished with no explanation. One
 * source of truth, so the count and the send agree.
 *
 * Unclaimed students have no `student_user` and are onboarded via access
 * sheets, not email.
 */
export function studentInviteIds(
  students: PortalStudent[],
  scopeIds: Set<string> | null
): string[] {
  const out: string[] = [];
  for (const student of students) {
    if (!inScope(student, scopeIds)) continue;
    if (student.student_user?.is_inviteable) {
      out.push(student.student_id);
    }
  }
  return out;
}

/**
 * Student ids for a bulk FAMILY invite (the "Invite N" action).
 *
 * A family qualifies when at least one linked parent is inviteable per AAS.
 * `some` rather than `every`: one reachable, not-yet-onboarded parent is enough
 * to make the family worth emailing, and the send path skips the rest anyway.
 * See {@link studentInviteIds} for why the rule lives on the backend.
 */
export function familyInviteIds(
  students: PortalStudent[],
  scopeIds: Set<string> | null
): string[] {
  const out: string[] = [];
  for (const student of students) {
    if (!inScope(student, scopeIds)) continue;
    if ((student.parents ?? []).some((parent) => parent.is_inviteable)) {
      out.push(student.student_id);
    }
  }
  return out;
}

/** Parse an AAS/ISO datetime string to epoch ms, or null if absent/invalid. */
function toMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/** Format an invite datetime for the re-invite confirm dialog. */
export function formatInviteDate(iso: string | null | undefined): string {
  const ms = toMs(iso);
  if (ms == null) return 'recently';
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Most recent invitation date (ISO string) across the chosen students for the
 * given audience, or null if none were ever invited. For 'student' this is the
 * student's own `last_invited_at`; for 'family' it's the latest across the
 * student's parents.
 */
export function mostRecentInviteDate(
  students: PortalStudent[],
  studentIds: Set<string>,
  audience: InviteAudience
): string | null {
  let bestMs: number | null = null;
  let best: string | null = null;
  const consider = (value: string | null | undefined) => {
    const ms = toMs(value);
    if (ms == null) return;
    if (bestMs == null || ms > bestMs) {
      bestMs = ms;
      best = value ?? null;
    }
  };
  for (const student of students) {
    if (!studentIds.has(student.student_id)) continue;
    if (audience === 'student') {
      consider(student.student_user?.last_invited_at);
    } else {
      for (const parent of student.parents ?? []) consider(parent.last_invited_at);
    }
  }
  return best;
}

/**
 * Whether any chosen student's most-recent invite (for the audience) is within
 * the re-invite confirm window — i.e. we should ask before sending again.
 * `now` is injectable for tests.
 */
export function shouldConfirmReinvite(
  students: PortalStudent[],
  studentIds: Set<string>,
  audience: InviteAudience,
  now: number = Date.now()
): boolean {
  const recent = mostRecentInviteDate(students, studentIds, audience);
  const ms = toMs(recent);
  return ms != null && now - ms < REINVITE_CONFIRM_WINDOW_MS;
}
